import { MapPin } from "lucide-react";

// Tutti i principali comuni siciliani in cui operiamo
const COMUNI = {
  Caltanissetta: [
    "Caltanissetta",
    "Gela",
    "Niscemi",
    "San Cataldo",
    "Mussomeli",
    "Mazzarino",
    "Riesi",
    "Sommatino",
    "Serradifalco",
    "Delia",
    "Santa Caterina Villarmosa",
    "Vallelunga Pratameno",
    "Acquaviva Platani",
    "Campofranco",
    "Marianopoli",
    "Milena",
    "Montedoro",
    "Resuttano",
    "Sutera",
    "Villalba",
    "Bompensiere",
  ],
  Agrigento: [
    "Agrigento",
    "Licata",
    "Canicattì",
    "Favara",
    "Sciacca",
    "Palma di Montechiaro",
    "Porto Empedocle",
    "Naro",
    "Ribera",
    "Aragona",
    "Casteltermini",
    "Cammarata",
    "Camastra",
    "Racalmuto",
    "Grotte",
    "Raffadali",
    "Realmonte",
  ],
  Enna: [
    "Enna",
    "Piazza Armerina",
    "Leonforte",
    "Nicosia",
    "Barrafranca",
    "Aidone",
    "Pietraperzia",
    "Troina",
    "Agira",
    "Centuripe",
    "Valguarnera Caropepe",
    "Calascibetta",
    "Catenanuova",
    "Regalbuto",
    "Assoro",
  ],
  Palermo: [
    "Palermo",
    "Monreale",
    "Bagheria",
    "Carini",
    "Partinico",
    "Termini Imerese",
    "Cefalù",
    "Misilmeri",
    "Corleone",
    "Lercara Friddi",
  ],
  Catania: [
    "Catania",
    "Acireale",
    "Caltagirone",
    "Paternò",
    "Mascalucia",
    "Misterbianco",
    "Adrano",
    "Belpasso",
    "Giarre",
    "Bronte",
    "Randazzo",
  ],
  "Altre province": [
    "Ragusa",
    "Modica",
    "Vittoria",
    "Comiso",
    "Scicli",
    "Siracusa",
    "Augusta",
    "Noto",
    "Avola",
    "Trapani",
    "Marsala",
    "Mazara del Vallo",
    "Castelvetrano",
    "Erice",
    "Messina",
    "Milazzo",
    "Barcellona Pozzo di Gotto",
    "Taormina",
  ],
};

export default function Zone() {
  return (
    <section
      id="zone-servite"
      data-testid="zone-section"
      className="relative py-24 lg:py-32 bg-brand-surface overflow-hidden"
    >
      <div className="absolute inset-0 dot-grid opacity-30 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7 reveal">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-bold text-brand-primary">
              <span className="w-8 h-px bg-brand-primary" />
              Zone servite
            </div>
            <h2 className="font-display font-extrabold text-[36px] sm:text-5xl lg:text-6xl tracking-tighter text-brand-ink mt-4 text-balance">
              In tutta la Sicilia.<br />
              <span className="text-brand-primary">In ogni comune.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pl-10 reveal reveal-delay-1">
            <p className="text-base sm:text-lg text-brand-body leading-relaxed">
              Operiamo con squadre e mezzi propri in <strong className="text-brand-ink">tutti i comuni della Sicilia</strong> —
              dalla città capoluogo al paese più piccolo dell'entroterra. Stesse tariffe trasparenti.
              Stessa cura. Stessa assicurazione totale.
            </p>
            <div className="mt-5 flex items-center gap-2 text-sm text-brand-body">
              <MapPin className="w-4 h-4 text-brand-primary" />
              <span>Oltre <strong className="text-brand-ink">120 comuni</strong> serviti ogni anno</span>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          {Object.entries(COMUNI).map(([province, list], pi) => (
            <div
              key={province}
              data-testid={`zone-province-${pi}`}
              className={`reveal reveal-delay-${(pi % 4) + 1}`}
            >
              <div className="flex items-baseline gap-3 mb-4">
                <h3 className="font-display font-bold text-lg sm:text-xl text-brand-ink">
                  {province === "Altre province" ? "Altre province" : `Provincia di ${province}`}
                </h3>
                <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-brand-body">
                  {list.length} comuni
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {list.map((c) => (
                  <a
                    key={c}
                    href="#preventivo"
                    data-testid={`zone-comune-${c.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                    className="group inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full bg-white border border-brand-line hover:border-brand-primary hover:bg-brand-primary hover:text-white transition-all duration-300 text-[13px] sm:text-sm font-semibold text-brand-ink"
                  >
                    <MapPin className="w-3 h-3 opacity-50 group-hover:opacity-100" />
                    {c}
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-14 rounded-2xl bg-white border border-brand-line p-6 sm:p-8 reveal text-center sm:text-left flex flex-col sm:flex-row items-center gap-5 justify-between">
          <div>
            <div className="font-display font-bold text-lg sm:text-xl text-brand-ink">
              Non vedi il tuo comune?
            </div>
            <div className="text-sm sm:text-base text-brand-body mt-1">
              Operiamo <strong className="text-brand-ink">ovunque in Sicilia</strong>, isole minori comprese.
              Chiamaci, ti diamo il preventivo in 60 minuti.
            </div>
          </div>
          <a
            href="#preventivo"
            data-testid="zone-cta-btn"
            className="w-full sm:w-auto shrink-0 inline-flex items-center justify-center h-12 px-6 rounded-full bg-brand-primary hover:bg-brand-ink text-white font-bold text-sm transition-colors"
          >
            Richiedi Preventivo
          </a>
        </div>
      </div>
    </section>
  );
}
